import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /**
   * Optional fallback override. The default is a friendly mobile-first
   * "something went wrong" screen with a Try again button.
   */
  fallback?: (args: { error: Error; reset: () => void }) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary. Catches every uncaught render/effect error
 * below it and renders a friendly screen so the user is never staring at
 * a blank page. Errors are logged to the console (CloudWatch RUM is out
 * of scope for Phase 1).
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface the stack in dev tools; production stacks are minified.
    console.error("[ErrorBoundary]", error, info);
  }

  private reset = () => this.setState({ error: null });

  override render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback({ error: this.state.error, reset: this.reset });
      }
      return <DefaultFallback error={this.state.error} reset={this.reset} />;
    }
    return this.props.children;
  }
}

function DefaultFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center px-4 text-center">
      <div className="mb-3 text-3xl">⚠️</div>
      <h1 className="text-xl font-semibold text-slate-900">Something went wrong</h1>
      <p className="mt-2 text-sm text-slate-600">
        The app hit an unexpected error. You can retry or go home.
      </p>
      <details className="mt-3 max-w-full overflow-auto text-left text-xs text-slate-500">
        <summary className="cursor-pointer">Technical details</summary>
        <pre className="mt-1 whitespace-pre-wrap break-words">{error.message}</pre>
      </details>
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={reset}
          className="min-h-[44px] rounded-md bg-teal-700 px-4 py-2 text-sm font-medium text-white hover:bg-teal-800"
        >
          Try again
        </button>
        <a
          href="/"
          className="min-h-[44px] rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Go home
        </a>
      </div>
    </div>
  );
}
